import type { SWRConfiguration } from 'swr'

import {
  resolvePolicy,
  type CachePolicyInput,
  type ServerCachePolicyInput,
} from './cache.policy'

export type ServerFetchCacheOptions = {
  cache?: 'force-cache' | 'no-store'
  next?: { revalidate?: number | false; tags?: string[] }
}

const MAX_CACHE_TAGS_PER_REQUEST = 128
const MAX_CACHE_TAG_LENGTH = 256
const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001F\u007F]/

export function resolveServerCache(
  input: ServerCachePolicyInput | undefined
): ServerFetchCacheOptions {
  const policy = resolvePolicy(input)
  if (policy.preset === 'NONE') return {}

  validateRevalidate(policy.revalidate)

  if (policy.serverCache === 'no-store') {
    if (policy.revalidate !== undefined || (policy.tags?.length ?? 0) > 0) {
      throw new Error(
        'no-store 정책에는 revalidate 또는 cache tag를 함께 사용할 수 없습니다.'
      )
    }

    return { cache: 'no-store' }
  }

  if (policy.serverCache === 'force-cache' && policy.revalidate === 0) {
    throw new Error('force-cache 정책에는 revalidate: 0을 사용할 수 없습니다.')
  }

  const next: { revalidate?: number | false; tags?: string[] } = {}
  if (policy.revalidate !== undefined) next.revalidate = policy.revalidate
  if (policy.tags && policy.tags.length > 0) {
    validateCacheTags(policy.tags)
    next.tags = [...policy.tags]
  }

  const result: ServerFetchCacheOptions = {}
  if (policy.serverCache) result.cache = policy.serverCache
  if (Object.keys(next).length > 0) result.next = next
  return result
}

export function resolveClientCache(input: CachePolicyInput | undefined): {
  cache?: RequestCache
} {
  const policy = resolvePolicy(input)
  if (policy.preset === 'NONE') return {}
  return policy.clientCache ? { cache: policy.clientCache } : {}
}

export function resolveSwrOptions<T>(
  input: CachePolicyInput | undefined,
  override?: SWRConfiguration<T>
): SWRConfiguration<T> {
  const policy = resolvePolicy(input)
  const base = (policy.swr ?? {}) as SWRConfiguration<T>
  return { ...base, ...override }
}

function validateCacheTags(tags: readonly string[]) {
  if (tags.length > MAX_CACHE_TAGS_PER_REQUEST) {
    throw new Error(
      `Next.js cache tag는 요청당 최대 ${MAX_CACHE_TAGS_PER_REQUEST}개까지 허용됩니다.`
    )
  }

  const invalidTag = tags.find((tag) => {
    return (
      tag.length === 0 ||
      tag.length > MAX_CACHE_TAG_LENGTH ||
      tag.trim() !== tag ||
      CONTROL_CHARACTER_PATTERN.test(tag)
    )
  })

  if (invalidTag !== undefined) {
    throw new Error(
      `Next.js cache tag는 앞뒤 공백·제어문자 없이 1~${MAX_CACHE_TAG_LENGTH}자여야 합니다.`
    )
  }

  if (new Set(tags).size !== tags.length) {
    throw new Error('Next.js cache tag는 요청 안에서 중복될 수 없습니다.')
  }
}

function validateRevalidate(revalidate: number | false | undefined) {
  if (
    typeof revalidate === 'number' &&
    (!Number.isInteger(revalidate) || revalidate < 0)
  ) {
    throw new Error('revalidate는 0 이상의 정수 또는 false여야 합니다.')
  }
}
